/**
 * "Retry notes" (TRUS-708): POST /calls/{id}/notes/retry for a call whose notes
 * failed with the recording still kept, or that is waiting for configuration.
 * Shown only when the backend says `notes_retryable` (staff only). On success
 * the call, its notes and the Calls & notes list refetch, so the card moves on
 * to "Notes are being prepared…"; a 409 / 410 is explained in words and the
 * button goes away (another try cannot help).
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RotateCcw } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { CallsApiError } from '@/services/callsApi';
import { useCallsApi } from './useCalls';

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

/** The words for a failed retry: the backend's 409 / 410 codes, else generic. */
export function retryNotesErrorText(t: TFn, err: unknown): string {
  const code = err instanceof CallsApiError ? err.code : null;
  switch (code) {
    case 'notes_already_processing':
      return t('messaging.calls.retryNotesAlreadyProcessing', 'The notes are already being made.');
    case 'notes_done':
      return t('messaging.calls.retryNotesDone', 'This call already has notes.');
    case 'recording_deleted':
      return t('messaging.calls.retryNotesRecordingDeleted', "The recording is no longer available, so notes can't be made.");
    default:
      return t('messaging.calls.retryNotesFailed', "Couldn't retry the notes. Try again.");
  }
}

interface CallNotesRetryButtonProps {
  callId: number;
  /** Blue (own staff) bubble: light text. */
  tone?: 'default' | 'inverted';
  className?: string;
}

export function CallNotesRetryButton({ callId, tone = 'default', className }: CallNotesRetryButtonProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);
  const inverted = tone === 'inverted';

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['call', api.kind, callId] }),
      queryClient.invalidateQueries({ queryKey: ['call-notes', api.kind, callId] }),
      queryClient.invalidateQueries({ queryKey: ['call-notes-list', api.kind] }),
    ]);

  const retry = useMutation({
    mutationFn: () => api.retryNotes(callId),
    onSuccess: (call) => {
      if (call) queryClient.setQueryData(['call', api.kind, callId], call);
      setMessage(t('messaging.calls.retryNotesQueued', 'Notes are being prepared…'));
      void refresh();
    },
    onError: (err) => {
      setMessage(retryNotesErrorText(t, err));
      // 409 / 410: retrying again cannot help. Keep the explanation on screen
      // (refetching now would unmount this with the button) and drop the button.
      if (err instanceof CallsApiError && (err.status === 409 || err.status === 410)) setSettled(true);
    },
  });

  if (api.kind !== 'staff') return null;
  const done = retry.isSuccess || settled;

  return (
    <div className={cn('flex flex-col items-start gap-0.5', className)} data-testid={`call-notes-retry-${callId}`}>
      {!done && (
        <button
          type="button"
          className={cn(
            'flex items-center gap-1 rounded-sm text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
            inverted ? 'text-white' : 'text-primary',
          )}
          onClick={() => {
            setMessage(null);
            retry.mutate();
          }}
          disabled={retry.isPending}
          data-testid={`call-notes-retry-button-${callId}`}
        >
          {retry.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
          {retry.isPending ? t('messaging.calls.retryingNotes', 'Retrying…') : t('messaging.calls.retryNotes', 'Retry notes')}
        </button>
      )}
      {message && (
        <span
          role="status"
          className={cn('text-xs', inverted ? 'text-white/80' : 'text-muted-foreground', retry.isSuccess && 'italic')}
          data-testid={`call-notes-retry-message-${callId}`}
        >
          {message}
        </span>
      )}
    </div>
  );
}

export default CallNotesRetryButton;
